/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import * as Yup from 'yup';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import QuestionResolutionStatusSelect from './QuestionResolutionStatusSelect';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Hooks
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Local API & Types
import {
  QuestionResolutionStatus,
  useUpdateQuestionResolutionStatusMutation,
} from '@app-types/blackroom-api';
import {
  DialogQuestionResolutionStatusProps,
  DialogQuestionResolutionStatusFormSchema,
} from './types';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogQuestionResolutionStatus({
  questionIds,
  scope,
  isSelectAll,
}: DialogQuestionResolutionStatusProps) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useWorkspace();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};

  // API
  const [updateQuestionResolutionStatus] =
    useUpdateQuestionResolutionStatusMutation();

  // Locale
  const { t } = useTranslation(['Common', 'Support']);

  const refetchQuestions = useRefetchQuestions();

  // Form
  const formSchema = Yup.object().shape({
    resolutionStatus: Yup.mixed<QuestionResolutionStatus>()
      .oneOf(Object.values(QuestionResolutionStatus))
      .required(),
  });
  const form = useForm<DialogQuestionResolutionStatusFormSchema>({
    initialValues: {
      resolutionStatus: QuestionResolutionStatus.Open,
    },
    validate: yupResolver(formSchema),
    onValuesChange: (formData) => {
      form.validate();
    },
  });

  /* ******************************** CALLBACKS ******************************* */

  async function onSubmit({
    ...formArgs
  }: DialogQuestionResolutionStatusFormSchema) {
    try {
      closeDialog?.('dialog-support-question-resolution-status');
      await updateQuestionResolutionStatus({
        variables: {
          updateQuestionResolutionStatusInput: {
            ...formArgs,
            questionIds,
            workspaceId: workspace.id,
            scope,
            isSelectAll,
          },
        },
      });
      refetchQuestions();
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Container
      w="20vw"
      onKeyDown={function cb(e) {
        switch (e.key) {
          case 'Enter':
            onSubmit(form.values);
            break;
          default:
            break;
        }
      }}
      fluid
    >
      <Stack align="stretch" h="100%">
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack>
            <QuestionResolutionStatusSelect
              label={t(
                'Support:PageSupport.supportActions.dialogQuestionResolutionStatus.fields.resolutionStatus.label',
              )}
              withAsterisk
              allowDeselect={false}
              selectedResolutionStatus={form.values.resolutionStatus}
              {...form.getInputProps('resolutionStatus')}
            />
          </Stack>
        </form>
        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.save'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={form.onSubmit(function cb(formValues) {
            onSubmit(formValues);
          })}
          onCancel={() => {
            dialogStore?.closeDialog?.(
              'dialog-support-question-resolution-status',
            );
          }}
          disabled={!form.isValid()}
        />
      </Stack>
    </Container>
  );
}

export default DialogQuestionResolutionStatus;
